/**
 * NEXUS UI - AreaChart
 * Full-featured area chart with grid, axes, and hover effects
 */

import React, { useMemo, useState } from 'react';
import type { BaseProps, NexusVariant, DataPoint } from './types';

export interface AreaChartProps extends BaseProps {
  data: DataPoint[];
  variant?: NexusVariant;
  width?: number;
  height?: number;
  showGrid?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showTooltip?: boolean;
  animated?: boolean;
  fill?: boolean;
  gridLines?: number;
  yAxisFormat?: (value: number) => string;
}

const variantColors: Record<NexusVariant, { stroke: string; fill: string; glow: string }> = {
  cyan: { stroke: '#00d4ff', fill: 'rgba(0, 212, 255, 0.1)', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { stroke: '#3b82f6', fill: 'rgba(59, 130, 246, 0.1)', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { stroke: '#a855f7', fill: 'rgba(168, 85, 247, 0.1)', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { stroke: '#00ff88', fill: 'rgba(0, 255, 136, 0.1)', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { stroke: '#ffaa00', fill: 'rgba(255, 170, 0, 0.1)', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { stroke: '#ff3366', fill: 'rgba(255, 51, 102, 0.1)', glow: 'rgba(255, 51, 102, 0.6)' },
};

export const AreaChart: React.FC<AreaChartProps> = ({
  data,
  variant = 'cyan',
  width = 400,
  height = 200,
  showGrid = true,
  showXAxis = true,
  showYAxis = true,
  showTooltip = true,
  animated = true,
  fill = true,
  gridLines = 5,
  yAxisFormat,
  className = '',
  style,
}) => {
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const colors = variantColors[variant];

  const padding = useMemo(() => ({ top: 20, right: 20, bottom: 40, left: 50 }), []);
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const { points, linePath, areaPath, yTicks, xTicks, minValue, maxValue } = useMemo(() => {
    if (data.length === 0) {
      return { points: [], linePath: '', areaPath: '', yTicks: [], xTicks: [], minValue: 0, maxValue: 100 };
    }

    const values = data.map(d => d.value);
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const range = maxVal - minVal || 1;
    const paddedMin = minVal - range * 0.1;
    const paddedMax = maxVal + range * 0.1;
    const paddedRange = paddedMax - paddedMin;

    const pts = data.map((d, index) => {
      const x = padding.left + (index / (data.length - 1)) * chartWidth;
      const y = padding.top + chartHeight - ((d.value - paddedMin) / paddedRange) * chartHeight;
      return { x, y, data: d };
    });

    // Create smooth curve
    const lineP = pts.reduce((acc, point, index) => {
      if (index === 0) return `M ${point.x} ${point.y}`;
      const prev = pts[index - 1];
      const cp1x = prev.x + (point.x - prev.x) / 3;
      const cp2x = prev.x + 2 * (point.x - prev.x) / 3;
      return `${acc} C ${cp1x} ${prev.y}, ${cp2x} ${point.y}, ${point.x} ${point.y}`;
    }, '');

    // Area path (closed)
    const areaP = `${lineP} L ${pts[pts.length - 1].x} ${padding.top + chartHeight} L ${pts[0].x} ${padding.top + chartHeight} Z`;

    // Y-axis ticks
    const yTickValues = Array.from({ length: gridLines }).map((_, i) => {
      const val = paddedMin + (paddedRange * i) / (gridLines - 1);
      return val;
    });

    // X-axis ticks
    const xTickIndices = data.length <= 6
      ? data.map((_, i) => i)
      : [0, Math.floor(data.length / 4), Math.floor(data.length / 2), Math.floor((3 * data.length) / 4), data.length - 1];

    return {
      points: pts,
      linePath: lineP,
      areaPath: areaP,
      yTicks: yTickValues,
      xTicks: xTickIndices,
      minValue: paddedMin,
      maxValue: paddedMax,
    };
  }, [data, chartWidth, chartHeight, padding, gridLines]);

  const gradientId = `area-gradient-${variant}-${Math.random().toString(36).slice(2, 9)}`;
  const glowId = `area-glow-${variant}-${Math.random().toString(36).slice(2, 9)}`;

  const formatYAxis = yAxisFormat || ((v: number) => v.toFixed(0));

  const aspectRatio = width / height;

  return (
    <div
      className={`nx-area-chart ${className}`}
      style={{
        position: 'relative',
        width: '100%',
        ...style,
      }}
    >
      <div style={{ position: 'relative', width: '100%', aspectRatio: `${aspectRatio}` }}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: 'visible' }}
          onMouseLeave={() => setHoveredPoint(null)}
        >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.stroke} stopOpacity="0.3" />
            <stop offset="100%" stopColor={colors.stroke} stopOpacity="0" />
          </linearGradient>
          <filter id={glowId}>
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Grid */}
        {showGrid && (
          <g className="nx-area-chart__grid">
            {/* Horizontal grid lines */}
            {yTicks.map((tick, index) => {
              const y = padding.top + chartHeight - ((tick - minValue) / (maxValue - minValue)) * chartHeight;
              return (
                <line
                  key={`h-${index}`}
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + chartWidth}
                  y2={y}
                  stroke="rgba(0, 212, 255, 0.08)"
                  strokeWidth={1}
                />
              );
            })}
            {/* Vertical grid lines */}
            {xTicks.map((tickIndex) => {
              const x = padding.left + (tickIndex / (data.length - 1)) * chartWidth;
              return (
                <line
                  key={`v-${tickIndex}`}
                  x1={x}
                  y1={padding.top}
                  x2={x}
                  y2={padding.top + chartHeight}
                  stroke="rgba(0, 212, 255, 0.08)"
                  strokeWidth={1}
                />
              );
            })}
          </g>
        )}

        {/* Y-axis */}
        {showYAxis && (
          <g className="nx-area-chart__y-axis">
            <line
              x1={padding.left}
              y1={padding.top}
              x2={padding.left}
              y2={padding.top + chartHeight}
              stroke="rgba(0, 212, 255, 0.2)"
              strokeWidth={1}
            />
            {yTicks.map((tick, index) => {
              const y = padding.top + chartHeight - ((tick - minValue) / (maxValue - minValue)) * chartHeight;
              return (
                <text
                  key={`y-label-${index}`}
                  x={padding.left - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill="var(--nx-text-ghost)"
                  fontSize="10"
                  fontFamily="var(--nx-font-mono)"
                >
                  {formatYAxis(tick)}
                </text>
              );
            })}
          </g>
        )}

        {/* X-axis */}
        {showXAxis && (
          <g className="nx-area-chart__x-axis">
            <line
              x1={padding.left}
              y1={padding.top + chartHeight}
              x2={padding.left + chartWidth}
              y2={padding.top + chartHeight}
              stroke="rgba(0, 212, 255, 0.2)"
              strokeWidth={1}
            />
            {xTicks.map((tickIndex) => {
              const point = data[tickIndex];
              const x = padding.left + (tickIndex / (data.length - 1)) * chartWidth;
              return (
                <text
                  key={`x-label-${tickIndex}`}
                  x={x}
                  y={padding.top + chartHeight + 16}
                  textAnchor="middle"
                  fill="var(--nx-text-ghost)"
                  fontSize="10"
                  fontFamily="var(--nx-font-mono)"
                >
                  {point?.label || tickIndex}
                </text>
              );
            })}
          </g>
        )}

        {/* Area fill */}
        {fill && (
          <path
            d={areaPath}
            fill={`url(#${gradientId})`}
          />
        )}

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          filter={`url(#${glowId})`}
          style={{
            strokeDasharray: animated ? '2000' : 'none',
            strokeDashoffset: animated ? '2000' : '0',
            animation: animated ? 'nx-border-trace 2s ease-out forwards' : 'none',
          }}
        />

        {/* Interactive overlay for hover */}
        {showTooltip &&
          points.map((point, index) => (
            <g key={`hover-${index}`}>
              <rect
                x={point.x - chartWidth / points.length / 2}
                y={padding.top}
                width={chartWidth / points.length}
                height={chartHeight}
                fill="transparent"
                onMouseEnter={() => setHoveredPoint(index)}
              />
              {/* Point */}
              {hoveredPoint === index && (
                <>
                  {/* Vertical line */}
                  <line
                    x1={point.x}
                    y1={padding.top}
                    x2={point.x}
                    y2={padding.top + chartHeight}
                    stroke={colors.stroke}
                    strokeWidth={1}
                    strokeDasharray="4 2"
                    opacity={0.5}
                  />
                  {/* Glow ring */}
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={8}
                    fill="none"
                    stroke={colors.stroke}
                    strokeWidth={1}
                    opacity={0.3}
                  />
                  {/* Point */}
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={5}
                    fill={colors.stroke}
                    filter={`url(#${glowId})`}
                  />
                </>
              )}
            </g>
          ))}
        </svg>
      </div>

      {/* Tooltip */}
      {showTooltip && hoveredPoint !== null && points[hoveredPoint] && (
        <div
          style={{
            position: 'absolute',
            left: points[hoveredPoint].x,
            top: points[hoveredPoint].y - 48,
            transform: 'translateX(-50%)',
            background: 'var(--nx-glass-dark)',
            border: `1px solid ${colors.stroke}`,
            borderRadius: '4px',
            padding: '8px 12px',
            boxShadow: `0 0 16px ${colors.glow}`,
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <div
            style={{
              fontFamily: 'var(--nx-font-mono)',
              fontSize: '14px',
              fontWeight: 600,
              color: colors.stroke,
            }}
          >
            {points[hoveredPoint].data.value}
          </div>
          {points[hoveredPoint].data.label && (
            <div
              style={{
                fontFamily: 'var(--nx-font-interface)',
                fontSize: '10px',
                color: 'var(--nx-text-tertiary)',
                marginTop: '2px',
              }}
            >
              {points[hoveredPoint].data.label}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AreaChart;
